import React, { useEffect, useState } from 'react';
import { api } from 'lib/api';
import { API_ENDPOINTS } from 'constants/endpoints';
import { usePopup } from 'context/PopupProvider';
import { Plus, Trash2, Edit2, X, RefreshCw, Gamepad, ExternalLink } from 'lucide-react';
import { listRowNumber, LIST_TD_NO_CLASS, LIST_TH_NO_CLASS, LIST_TABLE_CLASS, LIST_THEAD_CLASS, LIST_TBODY_CLASS, LIST_TH_CLASS, LIST_TD_CLASS } from 'lib/list-table';

interface GameRow {
  id: number;
  gameId: string;
  name: string;
  status: boolean;
  frontendUrl: string;
  backendUrl: string;
}

const emptyForm = {
  gameId: '',
  name: '',
  frontendUrl: 'http://localhost:3001',
  backendUrl: 'http://localhost:5002',
  status: true,
};

const GameManagement = () => {
  const { confirm } = usePopup();
  const [games, setGames] = useState<GameRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');

  const isEditMode = editingId !== null;

  const fetchGames = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await api.get<GameRow[]>(API_ENDPOINTS.ADMIN.GAMES.GET_ALL);
      if (res.success && res.data) setGames(res.data);
    } catch (err) {
      console.error('Failed to fetch games:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchGames();
  }, []);

  const closeModal = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    setError('');
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError('');
    setShowForm(true);
  };

  const openEdit = (game: GameRow) => {
    setEditingId(game.id);
    setForm({
      gameId: game.gameId,
      name: game.name,
      frontendUrl: game.frontendUrl,
      backendUrl: game.backendUrl,
      status: game.status,
    });
    setError('');
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const payload = {
        name: form.name,
        frontendUrl: form.frontendUrl,
        backendUrl: form.backendUrl,
        status: form.status,
      };
      const res = isEditMode
        ? await api.put(API_ENDPOINTS.ADMIN.GAMES.UPDATE(editingId!), payload)
        : await api.post(API_ENDPOINTS.ADMIN.GAMES.CREATE, { gameId: form.gameId, ...payload });

      if (res.success) {
        closeModal();
        fetchGames(true);
      } else {
        setError(res.message || (isEditMode ? 'Failed to update game.' : 'Failed to create game.'));
      }
    } catch {
      setError(isEditMode ? 'Error updating game registration.' : 'Error creating game registration.');
    }
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({
      title: 'Delete Game',
      message: 'Delete this game registration? Launch routing for this game will stop working until it is re-registered.',
      variant: 'danger',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
    });
    if (!ok) return;

    try {
      const res = await api.delete(API_ENDPOINTS.ADMIN.GAMES.DELETE(id));
      if (res.success) fetchGames(true);
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-muted font-medium">
        <RefreshCw className="animate-spin mr-2" size={20} /> Loading game registry...
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      <div className="bg-card p-6 rounded-2xl border border-line flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-brand-gold/10 text-brand-gold rounded-xl">
            <Gamepad size={24} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-heading">Game Registry</h3>
            <p className="text-sm text-muted">
              Register platform games and wire their frontend and backend service URLs.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-gold hover:bg-opacity-95 text-brand-dark font-bold rounded-xl transition-all shadow-sm text-sm"
        >
          <Plus size={18} /> Register Game
        </button>
      </div>

      <div className="bg-card rounded-2xl shadow-sm border border-line overflow-hidden">
        <div className={`${LIST_TD_CLASS} border-b border-line flex items-center justify-between gap-3`}>
          <div>
            <h4 className="font-bold text-heading">Registered Games</h4>
            <p className="text-xs text-muted mt-0.5">Used for launch routing across consumer integrations</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs bg-subtle text-muted px-2.5 py-1 rounded-full font-semibold">
              {games.length} {games.length === 1 ? 'Game' : 'Games'}
            </span>
            <button
              type="button"
              onClick={() => fetchGames(true)}
              disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-subtle hover:bg-subtle text-heading rounded-lg border border-line font-medium transition-all text-xs disabled:opacity-50"
              title="Refresh list"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} /> Refresh
            </button>
          </div>
        </div>

        {games.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-brand-gold/10 text-brand-gold mb-4">
              <Gamepad size={28} />
            </div>
            <h5 className="text-lg font-bold text-heading mb-1">No games registered yet</h5>
            <p className="text-sm text-muted max-w-md mx-auto mb-6">
              Add your first game to connect its frontend URL and backend service for launch flows.
            </p>
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-gold hover:bg-opacity-95 text-brand-dark font-bold rounded-xl transition-all shadow-sm text-sm"
            >
              <Plus size={16} /> Register Game
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className={LIST_TABLE_CLASS}>
              <thead className={LIST_THEAD_CLASS}>
                <tr>
                  <th className={`${LIST_TH_NO_CLASS} px-6 py-4`}>No.</th>
                  <th className={LIST_TH_CLASS}>Game ID</th>
                  <th className={LIST_TH_CLASS}>Name</th>
                  <th className={LIST_TH_CLASS}>Frontend URL</th>
                  <th className={LIST_TH_CLASS}>Backend URL</th>
                  <th className={LIST_TH_CLASS}>Status</th>
                  <th className={LIST_TH_CLASS}>Actions</th>
                </tr>
              </thead>
              <tbody className={LIST_TBODY_CLASS}>
                {games.map((g, index) => (
                  <tr key={g.id} className="hover:bg-subtle/50 transition-colors">
                    <td className={`${LIST_TD_NO_CLASS} px-6 py-4`}>{listRowNumber(1, index, 0)}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                      <span className="font-mono text-sm font-semibold text-brand-gold">{g.gameId}</span>
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm font-semibold text-heading`}>{g.name}</td>
                    <td className={`${LIST_TD_CLASS} max-w-[220px]`}>
                      <a
                        href={g.frontendUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group inline-flex items-center gap-1.5 text-sm text-muted hover:text-heading transition-colors truncate max-w-full"
                        title={g.frontendUrl}
                      >
                        <span className="truncate font-mono text-xs">{g.frontendUrl}</span>
                        <ExternalLink size={12} className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </a>
                    </td>
                    <td className={`${LIST_TD_CLASS} max-w-[220px]`}>
                      <span className="block truncate font-mono text-xs text-muted" title={g.backendUrl}>
                        {g.backendUrl}
                      </span>
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                      <span
                        className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          g.status
                            ? 'bg-green-500/10 text-green-700 border border-green-200'
                            : 'bg-red-500/10 text-red-700 border border-red-200'
                        }`}
                      >
                        {g.status ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm font-medium`}>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => openEdit(g)}
                          className="p-1 text-blue-600 hover:bg-blue-500/10 rounded"
                          title="Edit game"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(g.id)}
                          className="p-1 text-brand-danger hover:bg-red-500/10 rounded"
                          title="Delete game"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-brand-dark/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 backdrop-blur-sm animate-fadeIn">
          <div className="bg-card w-full max-w-lg max-h-[min(92dvh,720px)] sm:rounded-2xl rounded-t-2xl shadow-xl overflow-hidden animate-scaleIn border border-line flex flex-col">
            <div className="p-6 border-b border-line flex items-center justify-between bg-gradient-to-r from-brand-dark to-slate-900 text-white shrink-0">
              <div>
                <h4 className="font-bold text-lg text-brand-gold">
                  {isEditMode ? 'Edit Game Registration' : 'Register New Game'}
                </h4>
                <p className="text-xs text-faint">Frontend and backend service URLs</p>
              </div>
              <button type="button" onClick={closeModal} className="text-muted hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto overscroll-contain p-6 space-y-4">
              {error && (
                <div className="bg-red-500/10 text-brand-danger border border-red-100 px-4 py-3 rounded-xl text-sm font-semibold">
                  {error}
                </div>
              )}

              {!isEditMode && (
                <div>
                  <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Game ID</label>
                  <input
                    type="text"
                    required
                    placeholder="teen-patti"
                    value={form.gameId}
                    onChange={(e) => setForm({ ...form, gameId: e.target.value.trim().toLowerCase().replace(/\s+/g, '-') })}
                    className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border font-mono"
                  />
                  <p className="text-[11px] text-muted mt-1">Unique slug used in launch URLs — cannot be changed after creation.</p>
                </div>
              )}

              {isEditMode && (
                <div className="p-4 bg-subtle rounded-xl border border-line">
                  <p className="text-xs font-bold text-muted uppercase tracking-wider mb-1">Game ID</p>
                  <p className="font-mono text-sm font-semibold text-brand-gold">{form.gameId}</p>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Display Name</label>
                <input
                  type="text"
                  required
                  placeholder="Teen Patti"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Frontend URL</label>
                <input
                  type="url"
                  required
                  placeholder="http://localhost:3001"
                  value={form.frontendUrl}
                  onChange={(e) => setForm({ ...form, frontendUrl: e.target.value })}
                  className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border font-mono text-sm"
                />
                <p className="text-[11px] text-muted mt-1">Player-facing game client — opened after a successful launch.</p>
              </div>

              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Backend URL</label>
                <input
                  type="url"
                  required
                  placeholder="http://localhost:5002"
                  value={form.backendUrl}
                  onChange={(e) => setForm({ ...form, backendUrl: e.target.value })}
                  className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border font-mono text-sm"
                />
                <p className="text-[11px] text-muted mt-1">Game service base URL for real-time play.</p>
              </div>

              <div className="flex items-center justify-between p-4 bg-subtle rounded-xl border border-line">
                <div>
                  <h5 className="font-semibold text-sm text-heading">Active</h5>
                  <p className="text-xs text-muted">Inactive games are hidden from launch routing.</p>
                </div>
                <input
                  type="checkbox"
                  checked={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.checked })}
                  className="h-5 w-5 rounded border-line text-brand-gold focus:ring-brand-gold cursor-pointer"
                />
              </div>

              <div className="flex gap-4 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2.5 bg-subtle hover:bg-subtle text-body rounded-lg font-semibold border border-line transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 bg-brand-gold hover:bg-opacity-95 text-brand-dark rounded-lg font-bold transition-all text-sm shadow-sm"
                >
                  {isEditMode ? 'Save Changes' : 'Register Game'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameManagement;
